import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  LayoutDashboard,
  BookOpen,
  Video,
  Film,
  ClipboardList,
  LogOut,
  X,
  GraduationCap,
  Megaphone,
  MessagesSquare,
  Award,
  ChevronDown,
} from "lucide-react";
import { logoutUserThunk } from "../../../auth/authThunk";
import "../lms.css";

const BASE_NAV = [
  {
    section: "MAIN MENU",
    items: [
      { label: "Dashboard",    icon: LayoutDashboard, to: "/teacher-lms",             countKey: null, badge: null },
      { label: "My Courses",   icon: BookOpen,        to: "/teacher-lms/courses",      countKey: null, badge: null },
      { label: "Live Classes", icon: Video,           to: "/teacher-lms/live-classes",  countKey: null, badge: null },
      { label: "Recordings",   icon: Film,            to: "/teacher-lms/recordings",     countKey: null, badge: null },
      { label: "Audit Log",    icon: ClipboardList,   to: "/teacher-lms/audit-log",     countKey: null, badge: null },
      { label: "Attendance",   icon: ClipboardList,   to: "/teacher-lms/attendance",    countKey: null, badge: null },
      {
        label: "Examination",
        icon: ClipboardList,
        children: [
          { label: "Exams", icon: ClipboardList, to: "/teacher-lms/exams" },
        ],
      },
      {
        label: "Communications",
        icon: MessagesSquare,
        children: [
          { label: "Notice Board/Chat", icon: Megaphone, to: "/teacher-lms/notice-board" },
        ],
      },
    ],
  },
];

function getInitials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function TeacherLmsSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openMenus, setOpenMenus] = useState({
    Examination: true,
    Communications: false,
  });

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleLogout = async () => {
    await dispatch(logoutUserThunk());
    localStorage.removeItem("userData");
    localStorage.removeItem("userToken");
    navigate("/login");
  };

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  })();

  const userName   = userData?.admin_name || userData?.name || "Teacher User";
  const schoolName = userData?.school_name || "";
  const userRole   = "Teacher";

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside className={`lms-sidebar ${isOpen ? "lms-sidebar--open" : ""}`}>
      <div className="lms-sidebar-logo">
        <div className="lms-logo-icon">D</div>
        <div className="lms-logo-text">
          <span className="lms-logo-title">DocMaster LMS</span>
          <span className="lms-logo-sub">TEACHER</span>
        </div>
        <button className="lms-sidebar-close" onClick={onClose} title="Close menu">
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      <nav className="lms-sidebar-nav">
        {BASE_NAV.map((sec) => (
          <div key={sec.section}>
            <div className="lms-nav-section-label">{sec.section}</div>
            {sec.items.map((item) => {
              const Icon = item.icon;
              const isActive = item.to === "/teacher-lms"
                ? location.pathname === "/teacher-lms" || location.pathname === "/teacher-lms/"
                : location.pathname.startsWith(item.to);

              if (item.children) {
                const isChildActive = item.children.some((child) => location.pathname.startsWith(child.to));
                const isOpen = openMenus[item.label] ?? isChildActive;

                return (
                  <div key={item.label} style={{ marginBottom: "2px" }}>
                    <div
                      className="lms-nav-item"
                      onClick={() => toggleMenu(item.label)}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        userSelect: "none",
                        color: isChildActive ? "#1c498d" : undefined,
                        fontWeight: isChildActive ? "700" : undefined,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span className="lms-nav-icon">
                          <Icon size={16} strokeWidth={2} />
                        </span>
                        <span className="lms-nav-label">{item.label}</span>
                      </div>
                      <ChevronDown
                        size={14}
                        style={{
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                          color: "#94a3b8",
                        }}
                      />
                    </div>
                    {isOpen && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "1px", marginTop: "2px" }}>
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = location.pathname.startsWith(child.to);
                          return (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              className={`lms-nav-item ${childActive ? "active" : ""}`}
                              onClick={handleNavClick}
                              style={{
                                paddingLeft: "36px",
                                fontSize: "13px",
                                height: "36px",
                              }}
                            >
                              <span className="lms-nav-icon" style={{ opacity: childActive ? 1 : 0.8 }}>
                                <ChildIcon size={14} strokeWidth={2} />
                              </span>
                              <span className="lms-nav-label">{child.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isIndexActive = item.to === "/teacher-lms" && location.pathname === "/teacher-lms";

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/teacher-lms"}
                  onClick={handleNavClick}
                  className={`lms-nav-item ${isActive ? "active" : ""}`}
                >
                  <span className="lms-nav-icon">
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className="lms-nav-label">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="lms-sidebar-user">
        <div className="lms-user-avatar">{getInitials(userName)}</div>
        <div className="lms-user-info">
          <div className="lms-user-name">{userName}</div>
          <div className="lms-user-role">{userRole}</div>
          {schoolName && (
            <div className="lms-user-school" title={schoolName}>{schoolName}</div>
          )}
        </div>
        <button className="lms-logout-btn" onClick={handleLogout} title="Logout">
          <LogOut size={15} strokeWidth={2} />
        </button>
      </div>
    </aside>
  );
}
